/**
 * Project Manager API Service
 * Handles all PM-related API calls
 */

import api from './api';

const projectManagerApi = {
  // ============================================================================
  // PM ASSIGNMENT WORKFLOW
  // ============================================================================

  /**
   * PM applies to manage a project
   */
  async applyToProject(projectId, applicationData) {
    const response = await api.post(`/project-manager/apply/${projectId}`, applicationData);
    return response.data;
  },

  /**
   * Get available projects for PM to apply
   */
  async getAvailableProjects() {
    const response = await api.get('/project-manager/available-projects');
    return response.data;
  },

  /**
   * Get PM applications for a project (Owner view)
   */
  async getProjectApplications(projectId) {
    const response = await api.get(`/project-manager/applications/${projectId}`);
    return response.data;
  },

  /**
   * Owner responds to PM application (approve/reject)
   */
  async respondToApplication(requestId, action, responseData) {
    const response = await api.post(`/project-manager/applications/${requestId}/respond`, {
      action,
      ...responseData
    });
    return response.data;
  },

  /**
   * Get profile completion status
   */
  async getProfileCompleteness() {
    const response = await api.get('/project-manager/profile/completeness');
    return response.data;
  },

  /**
   * Get projects assigned to PM
   */
  async getMyProjects() {
    const response = await api.get('/project-manager/my-projects');
    return response.data;
  },

  // ============================================================================
  // AUTHORITY SETTINGS
  // ============================================================================

  /**
   * Get authority settings for a project
   */
  async getAuthoritySettings(projectId) {
    const response = await api.get(`/project-manager/authority/${projectId}`);
    return response.data;
  },

  /**
   * Update authority settings (Owner only)
   */
  async updateAuthoritySettings(projectId, settings) {
    const response = await api.put(`/project-manager/authority/${projectId}`, settings);
    return response.data;
  },

  // ============================================================================
  // SITE VISITS
  // ============================================================================

  /**
   * Check in at site
   */
  async checkIn(visitData) {
    const response = await api.post('/project-manager/site-visit/check-in', visitData);
    return response.data;
  },

  /**
   * Check out from site
   */
  async checkOut(visitId, checkoutData) {
    const response = await api.post(`/project-manager/site-visit/${visitId}/check-out`, checkoutData);
    return response.data;
  },

  /**
   * Get site visits for a project
   */
  async getSiteVisits(projectId) {
    const response = await api.get(`/project-manager/site-visits/${projectId}`);
    return response.data;
  },

  // ============================================================================
  // PAYMENT REQUESTS
  // ============================================================================

  /**
   * Create payment request
   */
  async createPaymentRequest(requestData) {
    const response = await api.post('/project-manager/payment-request', requestData);
    return response.data;
  },

  /**
   * Get payment requests for a project
   */
  async getPaymentRequests(projectId, status = null) {
    const url = status 
      ? `/project-manager/payment-requests/${projectId}?status=${status}`
      : `/project-manager/payment-requests/${projectId}`;
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Owner responds to payment request (approve/reject)
   */
  async respondToPaymentRequest(requestId, action, notes) {
    const response = await api.post(`/project-manager/payment-request/${requestId}/respond`, {
      action,
      notes
    });
    return response.data;
  },

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================

  /**
   * Get notifications
   */
  async getNotifications(unreadOnly = false) {
    const url = unreadOnly 
      ? '/project-manager/notifications?unreadOnly=true'
      : '/project-manager/notifications';
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Mark notification as read
   */
  async markNotificationRead(notificationId) {
    const response = await api.post(`/project-manager/notifications/${notificationId}/read`);
    return response.data;
  },

  /**
   * Mark all notifications as read
   */
  async markAllNotificationsRead() {
    const response = await api.post('/project-manager/notifications/read-all');
    return response.data;
  },

  // ============================================================================
  // DASHBOARD
  // ============================================================================

  /**
   * Get PM dashboard stats
   */
  async getDashboard() {
    const response = await api.get('/project-manager/dashboard');
    return response.data;
  },

  // ============================================================================
  // REPORTS & ACTIVITY
  // ============================================================================

  /**
   * Create a progress report
   */
  async createReport(reportData) {
    const response = await api.post('/project-manager/reports', reportData);
    return response.data;
  },

  /**
   * List reports for a project
   */
  async getReports(projectId) {
    const response = await api.get(`/project-manager/reports?projectId=${projectId}`);
    return response.data;
  },

  /**
   * Build the URL for streaming a report PDF
   */
  getReportPdfUrl(reportId) {
    return `${api.defaults.baseURL}/project-manager/reports/${reportId}/pdf`;
  },

  /**
   * Get the PM activity log for a project
   */
  async getActivity(projectId) {
    const response = await api.get(`/project-manager/activity?projectId=${projectId}`);
    return response.data;
  },

  // ============================================================================
  // COORDINATION (Phase 2)
  // ============================================================================

  /** Escrow milestones for one of the PM's projects */
  async getProjectMilestones(projectId) {
    const response = await api.get(`/project-manager/projects/${projectId}/milestones`);
    return response.data;
  },

  /** Sign off a milestone (mark reviewed by PM) */
  async signOffMilestone(milestoneId, notes) {
    const response = await api.post(`/project-manager/milestones/${milestoneId}/sign-off`, { notes });
    return response.data;
  },

  /** Assign (or recommend) a contractor for a project */
  async assignContractor(projectId, contractorId) {
    const response = await api.post(`/project-manager/projects/${projectId}/assign-contractor`, { contractorId });
    return response.data;
  },

  /** Assign (or recommend) an inspector to a milestone */
  async assignInspector(milestoneId, payload) {
    const response = await api.post(`/project-manager/milestones/${milestoneId}/assign-inspector`, payload);
    return response.data;
  },

  /** Contractors the PM can pick from (public verified-contractor listing) */
  async getAssignableContractors() {
    const response = await api.get('/contractors');
    return response.data;
  },
};

export default projectManagerApi;



